import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";

const Cart = () => {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    itemCount,
  } = useCart();

  const finalTotal = cartTotal;

  // --- WhatsApp Checkout Function ---
  const handleCheckout = () => {
    let message =
      "Hello, I would like to place an order for the following items:\n\n";

    cart.forEach((item) => {
      message += `- ${item.quantity}x ${item.name} ($${Number(item.price).toFixed(2)} each)\n`;
    });

    message += `\nTotal: $${finalTotal.toFixed(2)}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/96179362425?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  };

  // --- Empty Cart State ---
  if (cart.length === 0) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.emptyContainer}>
          <div style={{ fontSize: "4rem", marginBottom: "16px" }}>🍯</div>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: "700",
              marginBottom: "12px",
              color: "#111827",
            }}
          >
            Your Cart is Empty
          </h2>
          <p
            style={{
              color: "#6b7280",
              marginBottom: "28px",
              maxWidth: "400px",
            }}
          >
            Looks like you haven't added any BeeLuxe harvest goods to your cart
            yet.
          </p>
          <Link to="/shop" style={styles.shopBtn}>
            Explore the Collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Your Cart</h1>
        <span style={styles.itemBadge}>
          ({itemCount} {itemCount === 1 ? "item" : "items"})
        </span>
      </div>

      <div style={styles.cartGrid}>
        {/* Left Column: Product List */}
        <div style={styles.itemsList}>
          {cart.map((item) => (
            <div key={item.id} style={styles.cartItem}>
              {/* Product Thumbnail */}
              <div style={styles.imageWrapper}>
                {item.image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND}/uploads/${item.image}`}
                    alt={item.name}
                    style={styles.image}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = "none";
                      e.target.parentElement.innerHTML =
                        '<div style="font-size: 2.2rem;">🍯</div>';
                    }}
                  />
                ) : (
                  <div style={{ fontSize: "2.2rem" }}>🍯</div>
                )}
              </div>

              {/* Product Details */}
              <div style={styles.itemDetails}>
                <div style={styles.itemHeader}>
                  <Link to={`/products/${item.id}`} style={styles.itemName}>
                    {item.name}
                  </Link>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    style={styles.removeBtn}
                    title="Remove item"
                  >
                    ✕
                  </button>
                </div>

                <div style={styles.unitPrice}>
                  ${Number(item.price).toFixed(2)} each
                </div>

                <div style={styles.itemFooter}>
                  {/* Quantity Controls */}
                  <div style={styles.qtyContainer}>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      style={styles.qtyBtn}
                    >
                      -
                    </button>
                    <span style={styles.qtyText}>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      style={styles.qtyBtn}
                    >
                      +
                    </button>
                  </div>

                  {/* Line Total */}
                  <div style={styles.lineTotal}>
                    ${(Number(item.price) * item.quantity).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* List Actions */}
          <div style={styles.listActions}>
            <button onClick={clearCart} style={styles.clearBtn}>
              Clear Cart
            </button>
            <Link to="/shop" style={styles.continueLink}>
              ← Continue Shopping
            </Link>
          </div>
        </div>

        {/* Right Column: Order Summary */}
        <div style={styles.summaryCard}>
          <h2 style={styles.summaryTitle}>Order Summary</h2>
          <div style={styles.summaryRow}>
            <span>Subtotal</span>
            <span>${cartTotal.toFixed(2)}</span>
          </div>
          <div style={styles.divider} />
          <div style={{ ...styles.summaryRow, ...styles.totalRow }}>
            <span>Total</span>
            <span>${finalTotal.toFixed(2)}</span>
          </div>
          <button onClick={handleCheckout} style={styles.checkoutBtn}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            Order via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Styles ---
const styles = {
  pageWrapper: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "120px 24px 80px", // Navbar clearance
    minHeight: "100vh",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    textAlign: "center",
  },
  shopBtn: {
    display: "inline-block",
    padding: "14px 28px",
    backgroundColor: "#111827",
    color: "#ffffff",
    borderRadius: "8px",
    textDecoration: "none",
    fontWeight: "600",
    fontSize: "1rem",
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    gap: "12px",
    marginBottom: "32px",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: "16px",
  },
  title: {
    fontSize: "2.2rem",
    fontWeight: "700",
    color: "#111827",
    margin: 0,
  },
  itemBadge: {
    color: "#6b7280",
    fontSize: "1rem",
    fontWeight: "500",
  },
  cartGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "40px",
    alignItems: "start",
  },
  itemsList: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  cartItem: {
    display: "flex",
    gap: "20px",
    padding: "20px",
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
  },
  imageWrapper: {
    width: "90px",
    height: "90px",
    borderRadius: "8px",
    background: "#f9fafb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  itemDetails: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  itemName: {
    fontSize: "1.05rem",
    fontWeight: "600",
    color: "#111827",
    textDecoration: "none",
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: "0.9rem",
    padding: "0 4px",
  },
  unitPrice: {
    color: "#6b7280",
    fontSize: "0.875rem",
    marginTop: "2px",
  },
  itemFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
  },
  qtyContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    border: "1px solid white",
    borderRadius: "20px",
    padding: "2px 8px",
  },
  qtyBtn: {
    background: "none",
    border: "none",
    fontSize: "1.1rem",
    cursor: "pointer",
    color: "#374151",
    padding: "0 4px",
  },
  qtyText: {
    fontWeight: "600",
    fontSize: "0.95rem",
    minWidth: "16px",
    textAlign: "center",
    color: "black",
  },
  lineTotal: {
    fontWeight: "700",
    fontSize: "1.05rem",
    color: "#111827",
  },
  listActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
    padding: "0 4px",
  },
  clearBtn: {
    background: "none",
    border: "none",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "500",
    padding: 0,
  },
  continueLink: {
    color: "#4b5563",
    textDecoration: "none",
    fontSize: "0.9rem",
    fontWeight: "500",
  },
  summaryCard: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "28px",
  },
  summaryTitle: {
    fontSize: "1.3rem",
    fontWeight: "700",
    color: "#111827",
    margin: 0,
    marginBottom: "20px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
    fontSize: "0.95rem",
    color: "#4b5563",
  },
  totalRow: {
    fontSize: "1.2rem",
    fontWeight: "700",
    color: "#111827",
    marginTop: "12px",
    marginBottom: "24px",
  },
  divider: {
    height: "1px",
    backgroundColor: "#e5e7eb",
    margin: "16px 0",
  },
  freeShippingTip: {
    fontSize: "0.85rem",
    color: "#059669",
    backgroundColor: "#ecfdf5",
    padding: "8px 12px",
    borderRadius: "6px",
    marginTop: "8px",
    marginBottom: "8px",
  },
  checkoutBtn: {
    width: "100%",
    padding: "14px 16px", // Slightly adjusted padding for balance
    backgroundColor: "#25D366", // Official WhatsApp Green
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "1.05rem", // Slightly larger for better readability
    fontWeight: "600",
    cursor: "pointer",
    // Flexbox properties to align the icon and text
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px", // Space between icon and text
    boxShadow: "0 4px 6px -1px rgba(37, 211, 102, 0.2)", // Subtle green shadow for depth
    transition: "all 0.2s ease-in-out", // Smooth transition for hover effects (if moved to CSS)
  },
  guaranteeText: {
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#9ca3af",
    marginTop: "16px",
  },
};

export default Cart;
